import { useState, useCallback } from 'react';
import { Review, ReviewFilters as IReviewFilters, ReviewListParams, ReviewStatus } from '@/types';
import { useReviews, useModerateReview, useBulkModerate } from '@/services/review-service';
import { useToast } from '@/hooks/use-toast';
import { ReviewFilters } from './ReviewFilters';
import { ReviewDataTable } from './ReviewDataTable';
import { ReviewDetailDialog } from './ReviewDetailDialog';
import { BulkActionsBar } from './BulkActionsBar';
import { Pagination } from './Pagination';
import { Card } from '@/components/ui/card';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function ReviewListPage() {
  const [selectedReviews, setSelectedReviews] = useState<string[]>([]);
  const [selectedReviewIndex, setSelectedReviewIndex] = useState<number>(-1);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [filters, setFilters] = useState<IReviewFilters>({ status: [ReviewStatus.Pending] });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const { toast } = useToast();

  const params: ReviewListParams = {
    ...filters,
    page: currentPage,
    pageSize,
    sortBy,
    sortOrder,
  };

  const { data, isLoading, isError, refetch } = useReviews(params);
  const moderateMutation = useModerateReview();
  const bulkModerateMutation = useBulkModerate();

  const reviews = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;
  const totalItems = data?.total ?? 0;

  const selectedReview = selectedReviewIndex >= 0 ? reviews[selectedReviewIndex] ?? null : null;

  const handleFiltersChange = (newFilters: IReviewFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleSort = (newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };

  const handleApprove = async (reviewId: string, notes?: string) => {
    try {
      await moderateMutation.mutateAsync({ reviewId, action: 'approve', notes });
      toast({ title: 'Review approved' });
      setSelectedReviews((prev) => prev.filter((id) => id !== reviewId));
    } catch {
      toast({ title: 'Failed to approve review', variant: 'destructive' });
    }
  };

  const handleReject = async (reviewId: string, notes?: string) => {
    try {
      await moderateMutation.mutateAsync({ reviewId, action: 'reject', notes });
      toast({ title: 'Review rejected' });
      setSelectedReviews((prev) => prev.filter((id) => id !== reviewId));
    } catch {
      toast({ title: 'Failed to reject review', variant: 'destructive' });
    }
  };

  const handleBulkApprove = async () => {
    try {
      const response = await bulkModerateMutation.mutateAsync({
        reviewIds: selectedReviews,
        action: 'approve',
      });
      if (response.failed.length > 0) {
        toast({
          title: `${response.successful.length} approved, ${response.failed.length} failed`,
          variant: 'destructive',
        });
      } else {
        toast({ title: `${response.successful.length} reviews approved` });
      }
      setSelectedReviews([]);
    } catch {
      toast({ title: 'Bulk approve failed', variant: 'destructive' });
    }
  };

  const handleBulkReject = async () => {
    try {
      const response = await bulkModerateMutation.mutateAsync({
        reviewIds: selectedReviews,
        action: 'reject',
      });
      if (response.failed.length > 0) {
        toast({
          title: `${response.successful.length} rejected, ${response.failed.length} failed`,
          variant: 'destructive',
        });
      } else {
        toast({ title: `${response.successful.length} reviews rejected` });
      }
      setSelectedReviews([]);
    } catch {
      toast({ title: 'Bulk reject failed', variant: 'destructive' });
    }
  };

  const handleViewDetail = (reviewId: string) => {
    const index = reviews.findIndex((r) => r.reviewId === reviewId);
    if (index >= 0) {
      setSelectedReviewIndex(index);
      setIsDetailOpen(true);
    }
  };

  const handleNavigateReview = useCallback(
    (direction: 'prev' | 'next') => {
      setSelectedReviewIndex((current) => {
        if (direction === 'prev' && current > 0) return current - 1;
        if (direction === 'next' && current < reviews.length - 1) return current + 1;
        return current;
      });
    },
    [reviews.length]
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Review Moderation</h1>
        <p className="text-muted-foreground mt-2">
          Review and moderate customer reviews for your products
        </p>
      </div>

      <ReviewFilters filters={filters} onFiltersChange={handleFiltersChange} />

      {isLoading ? (
        <Card className="p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading reviews...</p>
          </div>
        </Card>
      ) : isError ? (
        <Card className="p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p className="text-muted-foreground">Failed to load reviews.</p>
            <Button variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Card>
      ) : (
        <>
          <ReviewDataTable
            reviews={reviews}
            selectedReviews={selectedReviews}
            onSelectionChange={setSelectedReviews}
            onApprove={handleApprove}
            onReject={handleReject}
            onViewDetail={handleViewDetail}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}

      <ReviewDetailDialog
        review={selectedReview}
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        onApprove={handleApprove}
        onReject={handleReject}
        onNavigate={handleNavigateReview}
        hasPrev={selectedReviewIndex > 0}
        hasNext={selectedReviewIndex < reviews.length - 1}
      />

      <BulkActionsBar
        selectedCount={selectedReviews.length}
        onBulkApprove={handleBulkApprove}
        onBulkReject={handleBulkReject}
        onClearSelection={() => setSelectedReviews([])}
      />
    </div>
  );
}
